import { parse as parseYaml } from 'yaml';

import { useHelmRelease } from '@/react/kubernetes/helm/helmReleaseQueries/useHelmRelease';

import { useAddonEnvironmentId } from '../../useAddonEnvironmentId';

import { addonNamespace } from './useAddonRelease';

/**
 * useAddonReleaseValues returns the add-on's actual (computed) Helm release
 * values as a nested object, so the read-only Configuration tab can show what
 * the release is really running with rather than the chart defaults.
 */
export function useAddonReleaseValues(id: string, { enabled = true } = {}) {
  const environmentQuery = useAddonEnvironmentId({ enabled: enabled && !!id });
  const environmentId = environmentQuery.data;

  const query = useHelmRelease(environmentId ?? 0, id, addonNamespace(id), {
    enabled: enabled && !!id && !!environmentId,
    select: (release) => parseComputedValues(release.values?.computedValues),
    // The release may be gone (removed externally / failed install); the form
    // falls back to chart defaults and shows an inline notice, so no toast.
    withErrorNotification: false,
    // a missing release won't appear on retry, so fail fast
    retry: 1,
  });

  return {
    ...query,
    /**
     * True once nothing is left to wait for: the read finished, or can never run
     * because no environment resolved. Callers seeding a form need the second
     * case or they wait forever.
     */
    isSettled:
      query.isFetched || (environmentQuery.isFetched && !environmentId),
  };
}

// computedValues is the effective values Helm rendered the release with (chart
// defaults merged with the user's overrides), returned as a YAML string.
function parseComputedValues(
  yaml: string | undefined
): Record<string, unknown> | undefined {
  if (!yaml) {
    return undefined;
  }
  const parsed: unknown = parseYaml(yaml);
  return isRecord(parsed) ? parsed : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
